import { normalizeText } from './textNormalization';

const SHEET_NAME_MAX_LENGTH = 31;
const SHEET_NAME_ILLEGAL_CHARS_REGEX = /[[\]:*?/\\]/g;
const DEFAULT_SHEET_NAME = 'Dữ liệu';
const ALL_FACULTIES_LABEL = 'Tất cả khoa';
const SLUG_NON_ALPHANUMERIC_REGEX = /[^a-z0-9]+/g;
const SLUG_TRIM_DASH_REGEX = /^-+|-+$/g;

export type ChartExcelRow = Record<string, string | number | null>;

export interface ChartExcelContext {
	khoa?: string | null;
	nam?: number | string | null;
	extra?: Record<string, string>;
}

export interface ChartExcelData {
	sheetName?: string;
	rows: ChartExcelRow[];
	context?: ChartExcelContext;
}

/** Diacritic-free kebab-case slug, e.g. "Top 10 Tỉnh/Thành" -> "top-10-tinh-thanh". */
export const toExcelFilenameSlug = (title: string): string => {
	return normalizeText(title)
		.replace(SLUG_NON_ALPHANUMERIC_REGEX, '-')
		.replace(SLUG_TRIM_DASH_REGEX, '');
};

/** 'YYYYMMDD-HHmmss' in Asia/Ho_Chi_Minh (UTC+7), independent of the host machine's timezone. */
export const formatExportTimestamp = (date: Date = new Date()): string => {
	const formatter = new Intl.DateTimeFormat('en-CA', {
		timeZone: 'Asia/Ho_Chi_Minh',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	});
	const parts = formatter.formatToParts(date);
	const get = (type: Intl.DateTimeFormatPartTypes): string => {
		const part = parts.find(p => p.type === type);
		if (!part) {
			throw new Error(`formatExportTimestamp: missing Intl date part "${type}"`);
		}
		// Intl can render midnight hour as "24" for hour12: false in some environments.
		return part.type === 'hour' && part.value === '24' ? '00' : part.value;
	};
	return `${get('year')}${get('month')}${get('day')}-${get('hour')}${get('minute')}${get('second')}`;
};

/** Excel sheet-name rules: strip illegal chars, cap at 31 chars, non-empty fallback. */
export const sanitizeSheetName = (name: string): string => {
	// Illegal characters are REPLACED, not deleted: deleting them welds words
	// together, so "Tỉnh/Thành x Khoa" became the meaningless "TỉnhThành x Khoa".
	// A separator keeps the title readable inside the 31-character budget.
	const cleaned = name
		.replace(SHEET_NAME_ILLEGAL_CHARS_REGEX, '-')
		.replace(/-{2,}/g, '-')
		.replace(/\s{2,}/g, ' ')
		.trim()
		.slice(0, SHEET_NAME_MAX_LENGTH)
		// Truncation can leave a dangling separator on the cut edge.
		.replace(/[-\s]+$/, '');
	return cleaned.length > 0 ? cleaned : DEFAULT_SHEET_NAME;
};

const khoaLabel = (khoa: ChartExcelContext['khoa']): string => (khoa ? khoa : ALL_FACULTIES_LABEL);

/** Filename tokens from filter context, e.g. "cntt-2025" or "tat-ca-khoa-2025". Empty string when ctx is absent. */
export const contextFilenameTokens = (ctx?: ChartExcelContext): string => {
	if (!ctx) {
		return '';
	}
	const tokens: string[] = [];
	const khoaToken = toExcelFilenameSlug(khoaLabel(ctx.khoa));
	if (khoaToken) {
		tokens.push(khoaToken);
	}
	if (ctx.nam !== undefined && ctx.nam !== null && `${ctx.nam}`.trim() !== '') {
		const namToken = toExcelFilenameSlug(`${ctx.nam}`);
		if (namToken) {
			tokens.push(namToken);
		}
	}
	return tokens.join('-');
};

/** Rows for the "Bộ lọc" sheet: Khoa, Năm, each extra filter, and export time. */
export const buildContextSheet = (ctx: ChartExcelContext): ChartExcelRow[] => {
	const rows: ChartExcelRow[] = [
		{ 'Bộ lọc': 'Khoa', 'Giá trị': khoaLabel(ctx.khoa) },
		{ 'Bộ lọc': 'Năm', 'Giá trị': ctx.nam === undefined || ctx.nam === null ? '' : ctx.nam },
	];
	if (ctx.extra) {
		for (const [label, value] of Object.entries(ctx.extra)) {
			rows.push({ 'Bộ lọc': label, 'Giá trị': value });
		}
	}
	rows.push({ 'Bộ lọc': 'Thời điểm xuất', 'Giá trị': formatExportTimestamp() });
	return rows;
};

/**
 * Writes a single-sheet (plus optional "Bộ lọc" filter sheet) .xlsx and triggers a browser
 * download. Safe no-op when there is no data to export. `xlsx` is loaded dynamically so it
 * stays out of the app's initial bundle.
 */
// `xlsx` giữ nguyên có chủ đích ở FE (backend đã gỡ, dùng exceljs cho file người
// dùng tải lên): dữ liệu ở đây là số liệu thống kê do chính app tạo ra, không
// phải file người dùng tải lên, nên CVE prototype-pollution/ReDoS của `xlsx`
// không áp dụng.
export const downloadChartExcel = async (data: ChartExcelData | undefined, baseFilename: string): Promise<void> => {
	if (!data || !data.rows || data.rows.length === 0) {
		return;
	}

	const XLSX = await import('xlsx');

	const worksheet = XLSX.utils.json_to_sheet(data.rows);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(data.sheetName ?? baseFilename));

	if (data.context) {
		const contextSheet = XLSX.utils.json_to_sheet(buildContextSheet(data.context));
		XLSX.utils.book_append_sheet(workbook, contextSheet, 'Bộ lọc');
	}

	const slug = toExcelFilenameSlug(baseFilename);
	const tokens = contextFilenameTokens(data.context);
	const timestamp = formatExportTimestamp();
	const nameParts = [slug, tokens, timestamp].filter(part => part.length > 0);
	XLSX.writeFile(workbook, `${nameParts.join('-')}.xlsx`);
};
